import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import layoutStyles from '../../styles/layout.module.css';
import styles from './LegalPage.module.css';

export interface LegalSection {
  id: string;
  heading: string;
  paragraphs: string[];
  list?: string[];
}

interface LegalPageProps {
  title: string;
  lastUpdated: string;
  tocLabel: string;
  backToTopLabel: string;
  sections: LegalSection[];
}

const LegalPage: React.FC<LegalPageProps> = ({ title, lastUpdated, tocLabel, backToTopLabel, sections }) => {
  const location = useLocation();

  useEffect(() => {
    const scrollTo = (location.state as { scrollTo?: string } | null)?.scrollTo;
    if (scrollTo) {
      const element = document.getElementById(scrollTo);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [location.state]);

  const toc = (
    <ul className={styles.tocList}>
      {sections.map((section) => (
        <li key={section.id}>
          <a href={`#${section.id}`}>{section.heading}</a>
        </li>
      ))}
    </ul>
  );

  return (
    <div className={styles.colorWrapper}>
      <div id="top" className={`${layoutStyles.container} ${styles.wrapper}`}>
        <header className={styles.header}>
          <h1 className={styles.title}>{title}</h1>
          <p className={styles.lastUpdated}>{lastUpdated}</p>
        </header>

        <div className={styles.layout}>
          <details className={styles.tocMobile}>
            <summary>{tocLabel}</summary>
            {toc}
          </details>

          <nav className={styles.tocDesktop} aria-label={tocLabel}>
            <h2>{tocLabel}</h2>
            {toc}
          </nav>

          <div className={styles.content}>
            {sections.map((section) => (
              <section key={section.id} id={section.id} className={styles.section}>
                <h2 className={styles.sectionHeading}>{section.heading}</h2>
                {section.paragraphs.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
                {section.list && (
                  <ul>
                    {section.list.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            <a className={styles.backToTop} href="#top">{backToTopLabel}</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
